import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';

import { Table } from '../../shared/table/table';
import { TableAction, TableColumn } from '../../shared/models/table-models';
import { ToastService } from '../../../core/shared/services/toast.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AppIcons } from '../../../core/icons/fa-icons';
import { MonitoringService } from '../shared/services/monitoring.service';
import { MonitoringItemDto } from '../shared/models/monitoring.model';
import { Popup } from '../../../core/shared/components/popup/popup';
import { PickListService } from '../../../core/shared/services/picklist.service';
import { PKL_GRP } from '../../../core/shared/models/picklist.model';

/** One client row of the monitoring landing screen's table. */
interface MonitoringClientRow {
  serialNo: number;
  clientSeq: number;
  clntNm: string;
  evntDt: string | null;
  totalAgreedActions: number;
  dueActions: number;
  compliant: number;
  partiallyCompliant: number;
  nonCompliant: number;
}

/**
 * Entry point of the Monitoring module.
 *
 * Every monitoring API is keyed by `clntSeq`, so the client has to be picked
 * before anything can be loaded — this screen is that picker.
 */
@Component({
  selector: 'app-monitoring-list',
  imports: [CommonModule, Table, Popup, TranslatePipe],
  templateUrl: './monitoring-list.html',
})
export class MonitoringList implements OnInit {
  constructor(
    private monitoringService: MonitoringService,
    private router: Router,
    private toast: ToastService,
    private pickListService: PickListService,
    private translate: TranslateService,
  ) {}

  data: MonitoringClientRow[] = [];
  loading = false;

  columns: TableColumn[] = [
    { key: 'serialNo', label: 'MONITORING.S_NO' },
    { key: 'clntNm', label: 'MONITORING.BORROWERS_NAME', align: 'left' },
    { key: 'evntDt', label: 'MONITORING.VISIT_DATE', type: 'date', align: 'left' },
    {
      key: 'totalAgreedActions',
      label: 'MONITORING.TOTAL_AGREED_ACTIONS',
      type: 'link',
      align: 'right',
      onClick: (row: MonitoringClientRow) => this.openMonitoringPopup(row),
    },
    {
      key: 'dueActions',
      label: 'MONITORING.DUE_ACTIONS',
      type: 'link',
      align: 'right',
      onClick: (row: MonitoringClientRow) =>
        this.openMonitoringPopup(row, this.DUE_ACTION_STS_KEY),
    },
    {
      key: 'compliant',
      label: 'MONITORING.COMPLIANT',
      type: 'link',
      align: 'right',
      onClick: (row: MonitoringClientRow) =>
        this.openMonitoringPopup(row, this.COMPLIANT_STS_KEY),
    },
    {
      key: 'partiallyCompliant',
      label: 'MONITORING.PARTIALLY_COMPLIANT',
      type: 'link',
      align: 'right',
      onClick: (row: MonitoringClientRow) =>
        this.openMonitoringPopup(row, this.PARTIALLY_COMPLIANT_STS_KEY),
    },
    {
      key: 'nonCompliant',
      label: 'MONITORING.NON_COMPLIANT',
      type: 'link',
      align: 'right',
      onClick: (row: MonitoringClientRow) =>
        this.openMonitoringPopup(row, this.NON_COMPLIANT_STS_KEY),
    },
  ];

  // ================= MONITORING RECORDS POPUP =================
  showMonitoringPopup = false;
  monitoringPopupTitle = '';
  monitoringPopupLoading = false;
  monitoringPopupRecords: MonitoringItemDto[] = [];
  private monitoringStatusMap: Record<number, string> = {};
  private readonly DUE_ACTION_STS_KEY = 2649;
  private readonly COMPLIANT_STS_KEY = 2648;
  private readonly PARTIALLY_COMPLIANT_STS_KEY = 2688;
  private readonly NON_COMPLIANT_STS_KEY = 2687;
  actions: TableAction[] = [
    {
      icon: AppIcons.view,
      tooltip: 'MONITORING.OPEN_MONITORING',
      class: 'text-indigo-600',
      onClick: (row: MonitoringClientRow) => this.openMonitoring(row),
    },
  ];

  ngOnInit(): void {
    this.loadClients();
    this.loadMonitoringStatuses();
  }

  /** Loads picklist group 38 (Monitoring Status) so `stsKey` can be shown as a label. */
  private loadMonitoringStatuses(): void {
    this.pickListService.loadCodesAsync([PKL_GRP.MONITORING_STATUS]).subscribe(() => {
      const map: Record<number, string> = {};

      this.pickListService.getOptionsSync(PKL_GRP.MONITORING_STATUS).forEach((option) => {
        map[Number(option.value)] = option.label;
      });

      this.monitoringStatusMap = map;
    });
  }

  monitoringStatusLabel(stsKey: number | null | undefined): string {
    if (stsKey === null || stsKey === undefined) {
      return '-';
    }

    return this.monitoringStatusMap[Number(stsKey)] || '-';
  }

  private loadClients(): void {
    this.loading = true;

    this.monitoringService.getMonitoringClients().subscribe({
      next: (response) => {
        const clients = Array.isArray(response) ? response : [];

        const rows: MonitoringClientRow[] = clients
          .filter((client) => Number(client?.delFlg ?? 0) !== 1)
          .map((client) => ({
            clientSeq: Number(client.clientSeq),
            clntNm: (client.clntNm || '').trim(),
            evntDt: client.lastEvntDt ?? null,
            totalAgreedActions: Number(client.totalAgrdActns ?? 0),
            dueActions: Number(client.dueActionCount ?? 0),
            compliant: Number(client.compliantCount ?? 0),
            partiallyCompliant: Number(client.partiallyCompliantCount ?? 0),
            nonCompliant: Number(client.nonCompliantCount ?? 0),
          }))
          .sort((first, second) =>
            first.clntNm.localeCompare(second.clntNm, undefined, { sensitivity: 'base' }),
          )
          .map((row, index) => ({ ...row, serialNo: index + 1 }));

        this.data = rows;

        this.loading = false;
      },

      error: (error: any) => {
        console.error('Unable to load the monitoring clients:', error);

        this.data = [];
        this.loading = false;
        this.toast.error('MONITORING.CLIENT_LIST_LOAD_FAILED');
      },
    });
  }

  openMonitoring(row: MonitoringClientRow): void {
    if (!row?.clientSeq) {
      this.toast.error('MONITORING.CLIENT_SEQ_UNAVAILABLE');
      return;
    }

    // Name is carried through only so the detail header can label the client
    // without a second lookup; the screen works without it.
    this.router.navigate(['/monitoring', row.clientSeq], {
      queryParams: { clntNm: row.clntNm },
    });
  }

  // ================= MONITORING RECORDS POPUP =================
  /**
   * @param stsFilter When given, only records whose `stsKey` matches are
   * shown in the popup (e.g. Due Actions -> {@link DUE_ACTION_STS_KEY}).
   */
  openMonitoringPopup(row: MonitoringClientRow, stsFilter?: number): void {
    if (!row?.clientSeq) {
      this.toast.error('MONITORING.CLIENT_SEQ_UNAVAILABLE');
      return;
    }

    this.monitoringPopupTitle = this.translate.instant('MONITORING.RECORDS_TITLE', { name: row.clntNm });
    this.monitoringPopupRecords = [];
    this.monitoringPopupLoading = true;
    this.showMonitoringPopup = true;

    this.monitoringService.getAllMonitoring(row.clientSeq).subscribe({
      next: (response) => {
        this.monitoringPopupRecords = (Array.isArray(response) ? response : [])
          .filter((item) => Number(item?.delFlg ?? 0) !== 1)
          .filter((item) => stsFilter === undefined || Number(item?.stsKey) === stsFilter);

        this.monitoringPopupLoading = false;
      },

      error: (error: any) => {
        console.error('Unable to load the monitoring records:', error);

        this.monitoringPopupRecords = [];
        this.monitoringPopupLoading = false;
        this.toast.error('MONITORING.RECORDS_LOAD_FAILED');
      },
    });
  }
}
